/**
 * CustomerSelector Component
 *
 * Shared customer selection dropdown.
 * Used by AddForm and EditForm for soft bookings.
 *
 * @example
 * ```tsx
 * <CustomerSelector
 *   label="Kunde"
 *   options={customerOptions}
 *   value={selectedCustomerOption}
 *   onChange={handleCustomerChange}
 *   loading={customersLoading}
 *   testId="add-form-customer"
 * />
 * ```
 */

import React from "react";
import classNames from "classnames";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import styles from "./CustomerSelector.module.css";

export interface CustomerSelectorProps {
  /** Label for the select */
  label: string;
  /** Placeholder text */
  placeholder?: string;
  /** Available customer options */
  options: SelectOption<number>[];
  /** Currently selected option */
  value: SelectOption<number>[];
  /** Change handler */
  onChange: (selected: SelectOption<number>[]) => void;
  /** Whether customers are loading */
  loading?: boolean;
  /** Test ID for the select */
  testId: string;
  /** Optional additional CSS class */
  className?: string;
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  label,
  placeholder = "Velg kunde...",
  options,
  value,
  onChange,
  loading = false,
  testId,
  className,
}) => {
  return (
    <div className={classNames(styles.customerSelector, className)}>
      <Select
        label={label}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
        searchable
        disabled={loading}
        data-testid={testId}
      />
    </div>
  );
};

CustomerSelector.displayName = "CustomerSelector";

export default CustomerSelector;
